import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon, CreditCardIcon, Bars3Icon } from '@heroicons/react/24/outline';
import Logo from './Logo';
import { navItems } from '../config/navItems';
import config from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const Navigation = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [wallet, setWallet] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch wallet when user is logged in
  useEffect(() => {
    if (!user || !user.id) {
      setWallet(null);
      return;
    }
    const fetchWallet = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(config.WALLET_API_URL, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWallet(data);
        } else {
          setWallet(null);
        }
      } catch {
        setWallet(null);
      }
    };
    fetchWallet();
  }, [user]);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const authItems = [
    { name: 'Đăng nhập', href: '/login' },
    { name: 'Đăng ký', href: '/register' },
  ];

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-dark-900/95 backdrop-blur-xl border-b border-cyan-500/10 shadow-lg shadow-cyan-500/5'
        : 'bg-dark-900/80 backdrop-blur-md border-b border-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Time Display */}
          <div className="hidden md:flex items-center text-sm font-mono mr-4 gap-4">
            <span className="px-3 py-1.5 rounded-lg bg-dark-800/50 border border-cyan-500/10 text-cyan-400/70">
              {currentTime.toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
              {' '}
              <span className="text-cyan-400">{currentTime.toLocaleTimeString('vi-VN')}</span>
            </span>
          </div>

          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <Logo size="sm" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map(item => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg text-ocean-100/70 hover:text-cyan-400 hover:bg-cyan-500/5"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg ${
                    isActive(item.href)
                      ? 'text-cyan-400 bg-cyan-500/10 border border-cyan-500/20'
                      : 'text-ocean-100/70 hover:text-cyan-400 hover:bg-cyan-500/5'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* Wallet & User */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Wallet Balance */}
            {user && wallet && (
              <Link
                to="/wallet"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 rounded-xl hover:from-cyan-500/30 hover:to-blue-500/30 transition-all shadow-glow-cyan/20 hover:shadow-glow-cyan"
              >
                <CreditCardIcon className="w-4 h-4" />
                <span className="font-bold font-mono">{formatCurrency(wallet.balance)}</span>
                <span className="text-cyan-300/70 text-xs">₫</span>
              </Link>
            )}

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(v => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-cyan-500/10 border border-cyan-500/20 text-ocean-100 font-medium transition-colors"
                  title="Tài khoản"
                >
                  <span className="inline-flex w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white items-center justify-center font-bold text-sm shadow-glow-cyan/30">
                    {user.name ? user.name[0] : user.email[0]}
                  </span>
                  <span className="hidden lg:inline max-w-[120px] truncate text-ocean-100/80">{user.name || user.email}</span>
                  <svg className={`w-4 h-4 text-cyan-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-dark-800/95 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-2xl z-50 p-2 text-sm animate-fade-in">
                    <div className="px-4 py-3 border-b border-cyan-500/10 mb-2">
                      <p className="font-semibold text-white truncate">{user.name || user.email}</p>
                      <p className="text-xs text-cyan-400/60 truncate">{user.email}</p>
                    </div>
                    <Link to="/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 hover:bg-cyan-500/10 rounded-xl transition-colors text-ocean-100/80 hover:text-cyan-400">
                      <span>🏠</span> Trang chủ
                    </Link>
                    <Link to="/wallet" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 hover:bg-cyan-500/10 rounded-xl transition-colors text-ocean-100/80 hover:text-cyan-400">
                      <span>💰</span> Ví điện tử
                    </Link>
                    <Link to="/profile" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 hover:bg-cyan-500/10 rounded-xl transition-colors text-ocean-100/80 hover:text-cyan-400">
                      <span>👤</span> Hồ sơ
                    </Link>
                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 hover:bg-cyan-500/10 rounded-xl transition-colors text-ocean-100/80 hover:text-cyan-400">
                        <span>⚙️</span> Quản trị
                      </Link>
                    )}
                    <div className="border-t border-cyan-500/10 mt-2 pt-2">
                      <button
                        onClick={() => { logout(); window.location.href = '/login'; }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-red-500/10 rounded-xl text-red-400 transition-colors"
                      >
                        <span>🚪</span> Đăng xuất
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {authItems.map(item => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                      item.href === '/register'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 shadow-glow-cyan/30 hover:shadow-glow-cyan'
                        : 'text-ocean-100/70 hover:text-cyan-400 border border-cyan-500/20 hover:border-cyan-500/40 hover:bg-cyan-500/5'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-cyan-400 hover:text-cyan-300 p-2 rounded-lg hover:bg-cyan-500/10 transition-colors"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-cyan-500/10 animate-fade-in">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-dark-800/95 backdrop-blur-xl">
              {navItems.map(item => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 text-base font-medium rounded-xl transition-colors text-ocean-100/70 hover:text-cyan-400 hover:bg-cyan-500/5"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 text-base font-medium rounded-xl transition-colors ${
                      isActive(item.href)
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'text-ocean-100/70 hover:text-cyan-400 hover:bg-cyan-500/5'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
            <div className="px-2 pt-2 pb-3 border-t border-cyan-500/10 bg-dark-800/95 backdrop-blur-xl">
              {user ? (
                <>
                  {/* Mobile Wallet */}
                  {wallet && (
                    <Link
                      to="/wallet"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-between px-4 py-3 mb-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 rounded-xl"
                    >
                      <span className="flex items-center gap-2">
                        <CreditCardIcon className="w-5 h-5" />
                        <span>Số dư ví</span>
                      </span>
                      <span className="font-bold font-mono">{formatCurrency(wallet.balance)} ₫</span>
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-base font-medium hover:bg-cyan-500/10 rounded-xl text-ocean-100/70 hover:text-cyan-400 transition-colors">
                    🏠 Trang chủ
                  </Link>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-base font-medium hover:bg-cyan-500/10 rounded-xl text-ocean-100/70 hover:text-cyan-400 transition-colors">
                    👤 Hồ sơ
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-base font-medium hover:bg-cyan-500/10 rounded-xl text-ocean-100/70 hover:text-cyan-400 transition-colors">
                      ⚙️ Quản trị
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); window.location.href = '/login'; }}
                    className="w-full text-left block px-4 py-3 text-base font-medium text-red-400 hover:bg-red-500/10 rounded-xl mt-2 transition-colors"
                  >
                    🚪 Đăng xuất
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  {authItems.map(item => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block px-4 py-3 text-base font-medium rounded-xl text-center transition-all ${
                        item.href === '/register'
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                          : 'text-ocean-100/70 hover:text-cyan-400 border border-cyan-500/20'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
